/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ForwardedRef,
} from "react";
import { mergeProps, useFocusRing, useFocusWithin, usePress } from "react-aria";

import { cssFns, useCss, type Style } from "@superweb/css";
import { useCurrencySymbol, useUnitSymbol } from "@superweb/intl";
import { icons, useTypo, useUiColors } from "@superweb/ui";

import { useMessage } from "#intl";

import { Tooltip, useTooltip } from "../../tooltip/tooltip";
import type { Column, SortingDirection, TypedColumn } from "../types";
import { useBaseCellStyle, zIndexMap, type BaseProps } from "./cells";

const SortIcon = ({ direction }: { direction?: SortingDirection }) => {
  const css = useCss();
  const className = css({
    ...cssFns.margin("0", "8px"),
    flexShrink: "0",
    width: "16px",
    height: "16px",
  });

  if (direction === "asc") return <icons.ArrowUp className={className} />;
  if (direction === "desc") return <icons.ArrowDown className={className} />;
  return <icons.ArrowSorting className={className} />;
};

const getAlign = <D,>(column: Column<D>) => {
  if ("align" in column && column.align) return column.align;
  if (
    column.type === "integer" ||
    column.type === "decimal" ||
    column.type === "percent" ||
    column.type === "currency" ||
    column.type === "unit"
  )
    return "end";
  return "start";
};

const useHeaderText = <D extends Object>(column: Column<D>) => {
  const message = useMessage();
  const getCurrencySymbol = useCurrencySymbol();
  const getUnitSymbol = useUnitSymbol();

  let headerText;
  let additional;

  if (
    (column.type === "currency" || column.type === "unit") &&
    column.displayUnitInHeader
  ) {
    additional =
      column.type === "currency"
        ? getCurrencySymbol(column.currency)
        : getUnitSymbol(column.unit);
  }

  if (column.type !== undefined && additional) {
    headerText = message({
      id: "d80202aa-2366-4420-ab5b-56f06b3a078d",
      context:
        "Common lib. The format of the table header, if there is additional information. For example, currency or units.",
      default: "{title}, {additionalTitle}",
      values: { title: column.header, additionalTitle: additional },
    });
  } else {
    headerText = column.header;
  }

  return headerText;
};

export const HeaderCell = <D extends Object>({
  column,
  /**
   * The index of the table column starts from 0.
   * It is used for the aria-colindex attribute by adding +1 to the value (i.e. aria-colindex starts with 1).
   */
  index,
  variant = "fill",

  sortState,
  onSortChange,

  isStickyFirstColumn,
  stickyFirstColumnShift,
  stickyShift,

  tabIndex,
  updateFocusPointer,
  addGridElement,
}: {
  column: Column<D>;
  index: number;
  variant?: "fill" | "ghost";

  sortState?: [string, SortingDirection];
  onSortChange?: (sort?: [string, SortingDirection]) => void;

  isStickyFirstColumn?: boolean;
  stickyFirstColumnShift?: number;
  stickyShift?: string;

  tabIndex?: number;
  updateFocusPointer: () => void;
  addGridElement: (
    rowIndex: number,
    colIndex: number,
    element: HTMLTableCellElement | null,
  ) => void;
}) => {
  const align = getAlign(column);

  if (column.type !== undefined) {
    return (
      <SlotHeaderCell
        column={column}
        index={index}
        variant={variant}
        sortState={sortState}
        onSortChange={onSortChange}
        isStickyFirstColumn={isStickyFirstColumn}
        stickyFirstColumnShift={stickyFirstColumnShift}
        stickyShift={stickyShift}
        tabIndex={tabIndex}
        updateFocusPointer={updateFocusPointer}
        addGridElement={addGridElement}
      />
    );
  }

  return (
    <UniversalHeaderCell
      id={column.id}
      /**
       * The index of the table column starts from 0.
       * It is used for the aria-colindex attribute by adding +1 to the value (i.e. aria-colindex starts with 1).
       */
      index={index}
      align={align}
      variant={variant}
      sortable={column.sortable}
      columnSort={column.sort}
      sortState={sortState}
      onSortChange={onSortChange}
      isStickyFirstColumn={isStickyFirstColumn}
      stickyFirstColumnShift={stickyFirstColumnShift}
      stickyShift={stickyShift}
      tabIndex={tabIndex}
      ref={(el) => addGridElement(0, index, el)}
      updateFocusPointer={updateFocusPointer}
      maxWidth={column.headerMaxWidth}
    >
      {column.header}
    </UniversalHeaderCell>
  );
};

const SlotHeaderCell = <D extends Object>({
  column,
  index,
  variant = "fill",

  sortState,
  onSortChange,

  isStickyFirstColumn,
  stickyFirstColumnShift,
  stickyShift,

  tabIndex,
  updateFocusPointer,
  addGridElement,
}: {
  column: TypedColumn<D>;

  /**
   * The index of the table column starts from 0.
   * It is used for the aria-colindex attribute by adding +1 to the value (i.e. aria-colindex starts with 1).
   */
  index: number;
  variant?: "fill" | "ghost";

  sortState?: [string, SortingDirection];
  onSortChange?: (sort?: [string, SortingDirection]) => void;

  isStickyFirstColumn?: boolean;
  stickyFirstColumnShift?: number;
  stickyShift?: string;

  tabIndex?: number;
  updateFocusPointer: () => void;
  addGridElement: (
    rowIndex: number,
    colIndex: number,
    element: HTMLTableCellElement | null,
  ) => void;
}) => {
  const align = getAlign(column);
  const headerText = useHeaderText(column);

  const targetRef = useRef<HTMLSpanElement | null>(null);
  const {
    triggerProps,
    tooltipProps,
    state: tooltipState,
  } = useTooltip(targetRef);
  const tooltipTriggerProps = mergeProps(triggerProps, { tabIndex: -1 });

  return (
    <UniversalHeaderCell
      id={column.id}
      index={index}
      align={align}
      variant={variant}
      sortable={column.sortable}
      columnSort={column.sort}
      sortState={sortState}
      onSortChange={onSortChange}
      isStickyFirstColumn={isStickyFirstColumn}
      stickyFirstColumnShift={stickyFirstColumnShift}
      stickyShift={stickyShift}
      tabIndex={tabIndex}
      ref={(el) => addGridElement(0, index, el)}
      updateFocusPointer={updateFocusPointer}
      onFocusChange={(isFocus) => {
        if (isFocus) {
          tooltipState.open();
        } else {
          tooltipState.close();
        }
      }}
      maxWidth={column.headerMaxWidth}
    >
      <span
        css={{
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {headerText}

        {column.headerTooltip ? (
          <>
            <span
              ref={targetRef}
              {...tooltipTriggerProps}
              css={{
                display: "inline-flex",
                cursor: "pointer",
                marginInlineStart: "4px",
                outlineStyle: "none",
              }}
            >
              <icons.InfoOutline width="20" height="20" />
            </span>

            {tooltipState.isOpen ? (
              <Tooltip
                state={tooltipState}
                tooltipProps={tooltipProps}
                targetRef={targetRef}
                variant="info"
              >
                {column.headerTooltip}
              </Tooltip>
            ) : null}
          </>
        ) : null}
      </span>
    </UniversalHeaderCell>
  );
};

const UniversalHeaderCell = forwardRef(
  (
    {
      children,
      align = "start",
      sortable = false,
      sortState,
      columnSort,
      onSortChange,
      id,
      index,
      tabIndex,
      updateFocusPointer,
      stickyShift,
      variant = "fill",
      stickyFirstColumnShift = 0,
      isStickyFirstColumn = false,
      onFocusChange,
      maxWidth,
    }: BaseProps & {
      sortable?: boolean;
      sortState?: [string, SortingDirection];
      columnSort?: "asc-desc" | "desc-asc" | "desc" | "asc";
      onSortChange?: (sort?: [string, SortingDirection]) => void;
      id: Column<unknown>["id"];
      stickyShift?: string;
      variant?: "fill" | "ghost";
      onFocusChange?: (isFocus: boolean) => void;
      maxWidth?: number;
    },
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    const typo = useTypo();
    const uiColors = useUiColors();

    const { focusProps: focusRingProps, isFocusVisible } = useFocusRing({
      within: true,
    });
    const { focusWithinProps: focusChangeProps } = useFocusWithin({
      onFocusWithinChange: onFocusChange,
    });
    const focusProps = mergeProps(focusChangeProps, focusRingProps);

    let zIndex =
      index === 0 ? zIndexMap.headerFirstColumn : zIndexMap.headerColumn;
    let insetInlineStart = `${stickyFirstColumnShift}px`;

    const style: Style = {
      ...useBaseCellStyle({ isFocusVisible, isHeaderCell: true, variant }),
      ...cssFns.padding("14px", "16px"),
      ...typo({
        level: "caption1",
        weight: "medium",
        density: "tight",
      }),
      zIndex,
      position: "sticky",
      ...(isStickyFirstColumn && { insetInlineStart }),
      top: stickyShift || "0",
      textAlign: align,

      ...(variant === "ghost"
        ? {
            backgroundColor: uiColors.backgroundMinor,
            color: uiColors.textMinor,
            height: "56px",
          }
        : {
            backgroundColor: uiColors.background,
            color: uiColors.text,
          }),
    };

    const currentDirection = sortState?.[0] === id ? sortState[1] : undefined;
    const [ariaSort, setAriaSort] = useState<
      "ascending" | "descending" | "none"
    >();

    useEffect(() => {
      if (!currentDirection) setAriaSort("none");
      if (currentDirection === "asc") setAriaSort("ascending");
      if (currentDirection === "desc") setAriaSort("descending");
    }, [currentDirection]);

    const { pressProps } = usePress({
      onPress: () => {
        if (!columnSort && !sortable) {
          return;
        }

        const directions = (columnSort ?? "desc-asc").split(
          "-",
        ) as SortingDirection[];

        const directionOrder =
          directions.findIndex((dir) => dir === currentDirection) + 1;
        const direction =
          directionOrder === directions.length
            ? undefined
            : directions[directionOrder % directions.length];
        onSortChange?.(direction && [id, direction]);
      },
    });

    if (columnSort || sortable) {
      return (
        <th
          ref={ref}
          aria-colindex={index + 1}
          aria-sort={ariaSort}
          css={style}
          {...focusProps}
          onClick={(e) => {
            updateFocusPointer();
            (e.currentTarget.firstElementChild as HTMLElement).focus();
          }}
        >
          <span
            css={{
              display: "inline-flex",
              alignItems: "center",
              outlineStyle: "none",
              cursor: "pointer",

              ...(maxWidth && {
                maxWidth: `${maxWidth}px`,
                width: "max-content",
                whiteSpace: "normal",
              }),
            }}
            tabIndex={tabIndex}
            {...pressProps}
          >
            {children}
            <SortIcon direction={currentDirection} />
          </span>
        </th>
      );
    }

    return (
      <th
        css={style}
        aria-colindex={index}
        ref={ref}
        tabIndex={tabIndex}
        {...focusProps}
      >
        {children}
      </th>
    );
  },
);
